import styled from "@emotion/styled";
import { Box, Typography } from "@mui/material";
import { useCallback, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import {
  GET_CONNECTED_APPS_QUERY_KEY,
  useGetActionsApps,
} from "../../queries/actions-apps/useGetActionsApps";
import { AppsControllerListAllApps200ResponseInner } from "@shared/apis/sidekick/generated";
import { getActionsAppsClientConfig } from "@shared/apis/sidekick/api-factories";
import { useDisconnectActionsApp } from "../../queries/actions-apps/useDisconnectActionsApp";
import { useAuth } from "@shared/hooks/useAuth";
import { useUpdateUserCommunicationPreferences } from "@queries/profile/useUpdateUserCommunicationPreferences";
import { ComputedCommunicationPreferences } from "@shared/apis/sidekick/generated";
import { dashboardRoutePaths } from "@routes/dashboard.routes";
import { appConfig } from "@shared/utils/appConfig";
import { useUIStore } from "@store/ui.store";
import { toast } from "@shared/services/toast";
import { textFormatter } from "@shared/utils/text-formatter";
import { useEnableGoogleContactsExtraction } from "./useEnableGoogleContactsExtraction";

const SuccessText = styled(Typography)`
  font-size: ${(p) => p.theme.text.fontSizes.l};
`;

type Params = {
  highlightAppId?: string;
};

type ConnectionState = "connected" | "not-connected" | "expired" | "connecting";

export const useConnectedAppsLogic = ({
  highlightAppId: highlightAppIdProp,
}: Params = {}) => {
  const [connectionOverrides, setConnectionOverrides] = useState<
    Record<string, ConnectionState>
  >({});

  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [dismissedHighlight, setDismissedHighlight] = useState<string | null>(
    null,
  );
  const rawHighlightAppId =
    highlightAppIdProp ?? searchParams.get("highlight") ?? null;
  const highlightAppId =
    rawHighlightAppId === dismissedHighlight ? null : rawHighlightAppId;
  const queryClient = useQueryClient();
  const connectedAppsQuery = useGetActionsApps();
  const connectedApps = connectedAppsQuery.data || [];
  const actionsAppsDisconnectMutation = useDisconnectActionsApp();
  const navigate = useNavigate();
  const confirmDialog = useUIStore((state) => state.confirmDialog);
  const { enableGoogleContactsExtraction } =
    useEnableGoogleContactsExtraction();

  const setConnectionOverride = useCallback(
    (id: string, connection: ConnectionState) => {
      setConnectionOverrides((prev) => ({ ...prev, [id]: connection }));
    },
    [],
  );

  const clearConnectionOverride = useCallback((id: string) => {
    setConnectionOverrides((prev) => {
      if (!(id in prev)) return prev;
      const { [id]: _removed, ...rest } = prev;
      return rest;
    });
  }, []);

  const updateUserCommunicationPreferencesMutation =
    useUpdateUserCommunicationPreferences({
      onSettled: (params) => {
        clearConnectionOverride(
          params.type === "smsChat" ? "smsChat" : "google",
        );
      },
    });

  const handleConnectApp = useCallback(
    async (app: {
      id: string | undefined;
      connect: boolean;
      swapContext?: { disconnectedAppName: string; newAppName: string };
    }) => {
      if (!app.connect) {
        await actionsAppsDisconnectMutation.mutateAsync({ appId: app.id! });
        setConnectionOverride(app.id!, "not-connected");
        return;
      }

      const notifyIfSwapFailed = (connected: boolean) => {
        if (!app.swapContext || connected) return;
        toast.error(
          `${app.swapContext.disconnectedAppName} was disconnected, but ${app.swapContext.newAppName} wasn't connected. Please reconnect one of them.`,
          { toastId: `swap-failed-${app.id}` },
        );
      };

      const basePath = getActionsAppsClientConfig().basePath;
      const popup = window.open(
        `${basePath}/actions/apps/${app.id}/connection?redirect=${window.location.origin}/account/app-connection/${app.id}/success`,
        "_blank",
        "width=520,height=620",
      );

      try {
        if (popup) {
          const timer = setInterval(() => {
            if (popup.closed) {
              clearInterval(timer);
              (async () => {
                await queryClient.invalidateQueries({
                  queryKey: [GET_CONNECTED_APPS_QUERY_KEY],
                });
                const freshApps = queryClient.getQueryData<
                  AppsControllerListAllApps200ResponseInner[]
                >([GET_CONNECTED_APPS_QUERY_KEY]);
                const connected =
                  freshApps?.find((a) => a.id === app.id)?.connection ===
                  "connected";
                if (app.id === "google" && connected) {
                  enableGoogleContactsExtraction();
                }
                notifyIfSwapFailed(connected);
                setTimeout(() => {
                  clearConnectionOverride(app.id!);
                }, 500);
              })();
            }
          }, 1000);
        } else {
          console.warn(
            "Popup could not be opened. Possibly blocked by browser.",
          );
          notifyIfSwapFailed(false);
          clearConnectionOverride(app.id!);
        }
      } catch {
        console.error("Could not detect if popup was closed.");
      } finally {
        queryClient.invalidateQueries({
          queryKey: [GET_CONNECTED_APPS_QUERY_KEY],
        });
      }
    },
    [
      actionsAppsDisconnectMutation,
      clearConnectionOverride,
      enableGoogleContactsExtraction,
      queryClient,
      setConnectionOverride,
    ],
  );

  const handleUpdateSmsChatConnection = useCallback(
    async (app: { id: string | undefined; connect: boolean }) => {
      if (app.id === "smsChat") {
        if (app.connect && !user.phoneNumber) {
          navigate(
            `${dashboardRoutePaths.profile.originalPath}?field_focus=phoneNumber`,
            {
              state: {
                smsChatEnabled: true,
              },
            },
          );
          return;
        }
        await updateUserCommunicationPreferencesMutation.mutateAsync({
          type: "smsChat",
          enabled: app.connect,
        });
        setTimeout(() => {
          if (app.connect) {
            confirmDialog({
              title: "Chat via SMS",
              content: (
                <Box>
                  <SuccessText>
                    SMS Chat has been enabled successfully!
                  </SuccessText>
                  {appConfig.SIDEKICK_SMS_CHAT_NUMBER && (
                    <SuccessText mt={1}>
                      Send a text to{" "}
                      <strong>
                        {textFormatter.formatPhoneNumber(
                          appConfig.SIDEKICK_SMS_CHAT_NUMBER,
                        )}
                      </strong>{" "}
                      to get started.
                    </SuccessText>
                  )}
                </Box>
              ),
              confirmButtonText: "Ok",
              cancelButtonText: false,
            });
          } else {
            toast.info("Chat via SMS has been disabled.", {
              toastId: "update-user-communication-preferences-smsChat-disabled",
            });
          }
        }, 500);
      }
    },
    [
      navigate,
      updateUserCommunicationPreferencesMutation,
      user.phoneNumber,
      confirmDialog,
    ],
  );

  const dismissHighlight = useCallback(() => {
    setDismissedHighlight(rawHighlightAppId);
    if (searchParams.get("highlight")) {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.delete("highlight");
          return next;
        },
        { replace: true },
      );
    }
  }, [rawHighlightAppId, searchParams, setSearchParams]);

  const resolveCommunicationPreferencesConnection = (
    appId: keyof ComputedCommunicationPreferences,
  ) => {
    if (connectionOverrides[appId]) {
      return connectionOverrides[appId];
    }
    return user.communicationPreferences[appId]?.isEnabled
      ? "connected"
      : "not-connected";
  };

  return {
    user,
    connectedApps,
    connectedAppsQuery,
    actionsAppsDisconnectMutation,
    connectionOverrides,
    setConnectionOverride,
    highlightAppId,
    handleConnectApp,
    handleUpdateSmsChatConnection,
    dismissHighlight,
    resolveCommunicationPreferencesConnection,
  };
};
